import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  RefreshCw,
  Bell,
  SlidersHorizontal,
  ClipboardList,
  Loader2,
  Check,
  ChevronDown,
  Goal,
  Send,
  Search,
} from 'lucide-react';
import {
  useWcMatches,
  useWcSaveResult,
  useWcSaveLive,
  useWcTeamPlayers,
  useWcScoring,
  useWcSaveScoring,
  useWcRecompute,
  useWcNotify,
  type WcMatch,
  type WcPhase,
  type WcScoringConfig,
} from '@/lib/queries';
import { PageTransition } from '@/components/ui/motion';
import {
  Flag,
  PHASE_SHORT,
  PHASE_ORDER,
  formatKickoff,
  slotLabel,
  useViewerTimezone,
} from '@/components/mundial/common';
import { cn } from '@/lib/cn';

type AdminTab = 'resultados' | 'puntos' | 'avisos';

export function MundialAdminPage() {
  const [tab, setTab] = useState<AdminTab>('resultados');
  const recompute = useWcRecompute();
  const [recomputed, setRecomputed] = useState(false);

  const TABS: { key: AdminTab; label: string; icon: any }[] = [
    { key: 'resultados', label: 'Resultados', icon: ClipboardList },
    { key: 'puntos', label: 'Tabla de puntos', icon: SlidersHorizontal },
    { key: 'avisos', label: 'Notificaciones', icon: Bell },
  ];

  return (
    <PageTransition className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/mundial"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-font-secondary transition hover:border-primary hover:text-primary"
          >
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-font-primary">Administración · Mundial 2026</h1>
            <p className="text-sm text-font-secondary">Carga resultados, ajusta puntos y envía avisos</p>
          </div>
        </div>
        <button
          onClick={() =>
            recompute.mutate(undefined, {
              onSuccess: () => {
                setRecomputed(true);
                setTimeout(() => setRecomputed(false), 2000);
              },
            })
          }
          disabled={recompute.isPending}
          className="flex h-10 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-font-primary transition hover:border-primary hover:text-primary disabled:opacity-60"
        >
          {recompute.isPending ? (
            <Loader2 size={15} className="animate-spin" />
          ) : recomputed ? (
            <Check size={15} className="text-accent" />
          ) : (
            <RefreshCw size={15} />
          )}
          {recomputed ? 'Recalculado' : 'Recalcular todo'}
        </button>
      </div>

      <div className="flex gap-1 rounded-lg border border-border bg-white p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'flex h-10 flex-1 items-center justify-center gap-2 rounded-md text-sm font-semibold transition',
              tab === t.key ? 'bg-primary text-white shadow-sm' : 'text-font-secondary hover:bg-bg',
            )}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'resultados' && <ResultsTab />}
      {tab === 'puntos' && <ScoringTab />}
      {tab === 'avisos' && <NotifyTab />}
    </PageTransition>
  );
}

function ResultsTab() {
  const [phase, setPhase] = useState<WcPhase>('GROUP');
  const { data, isLoading } = useWcMatches({ phase });
  const [openId, setOpenId] = useState<string | null>(null);
  const items = data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-white p-1">
        {PHASE_ORDER.map((p) => (
          <button
            key={p}
            onClick={() => {
              setPhase(p);
              setOpenId(null);
            }}
            className={cn(
              'h-8 rounded-md px-3 text-xs font-semibold transition',
              phase === p ? 'bg-primary text-white shadow-sm' : 'text-font-secondary hover:bg-bg',
            )}
          >
            {PHASE_SHORT[p]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-xl border border-border brand-shimmer" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((m) => (
            <AdminMatchRow
              key={m.id}
              match={m}
              open={openId === m.id}
              onToggle={() => setOpenId(openId === m.id ? null : m.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminMatchRow({
  match,
  open,
  onToggle,
}: {
  match: WcMatch;
  open: boolean;
  onToggle: () => void;
}) {
  const k = formatKickoff(match.kickoff, useViewerTimezone());
  const finished = match.status === 'FINISHED';
  const hasTeams = !!match.homeTeam && !!match.awayTeam;

  return (
    <div className={cn('rounded-xl border bg-white', open ? 'border-primary/40 shadow-sm' : 'border-border')}>
      <button onClick={onToggle} className="flex w-full items-center gap-3 px-4 py-3 text-left">
        <span className="w-8 text-xs font-bold text-font-tertiary">#{match.number}</span>
        <span className="rounded bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-font-secondary">
          {PHASE_SHORT[match.phase]}
          {match.group ? ` ${match.group}` : ''}
        </span>
        <div className="flex min-w-0 flex-1 items-center justify-center gap-2 text-sm">
          <span className="flex min-w-0 flex-1 items-center justify-end gap-1.5 truncate font-medium">
            <span className="truncate">{match.homeTeam ? match.homeTeam.nameEs : slotLabel(match.homeSlot)}</span>
            <Flag iso2={match.homeTeam?.iso2} className="h-4 w-6 flex-shrink-0" />
          </span>
          <span className="flex-shrink-0 font-bold text-font-primary">
            {finished ? `${match.homeScore} - ${match.awayScore}` : 'vs'}
          </span>
          <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate font-medium">
            <Flag iso2={match.awayTeam?.iso2} className="h-4 w-6 flex-shrink-0" />
            <span className="truncate">{match.awayTeam ? match.awayTeam.nameEs : slotLabel(match.awaySlot)}</span>
          </span>
        </div>
        {finished && (
          <span className="flex-shrink-0 rounded-full bg-accent-light px-2 py-0.5 text-[10px] font-bold text-accent">
            Final
          </span>
        )}
        <ChevronDown size={16} className={cn('flex-shrink-0 text-font-tertiary transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="border-t border-border-light p-4">
          {hasTeams ? (
            <AdminResultForm match={match} />
          ) : (
            <p className="text-sm text-font-tertiary">
              Los equipos de este partido aún no están definidos. Carga primero los resultados de la
              ronda anterior y se asignarán automáticamente. ({slotLabel(match.homeSlot)} vs{' '}
              {slotLabel(match.awaySlot)})
            </p>
          )}
          <p className="mt-2 text-[11px] text-font-tertiary">
            {k.weekday}, {k.date} · {k.time} · {match.city}
          </p>
        </div>
      )}
    </div>
  );
}

/** Normaliza para buscar sin acentos ni mayúsculas. */
function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function AdminResultForm({ match }: { match: WcMatch }) {
  const save = useWcSaveResult();
  const saveLive = useWcSaveLive();
  const homePlayers = useWcTeamPlayers(match.homeTeam?.id);
  const awayPlayers = useWcTeamPlayers(match.awayTeam?.id);

  const [home, setHome] = useState(match.homeScore ?? 0);
  const [away, setAway] = useState(match.awayScore ?? 0);
  const [homePens, setHomePens] = useState<number | ''>(match.homePens ?? '');
  const [awayPens, setAwayPens] = useState<number | ''>(match.awayPens ?? '');
  const [scorers, setScorers] = useState<Record<string, number>>({});
  const [playerFilter, setPlayerFilter] = useState('');
  const [liveMin, setLiveMin] = useState<number | ''>(match.liveMinute ?? '');
  const [saved, setSaved] = useState(false);
  const isLive = match.status === 'LIVE';

  // precargar goleadores existentes
  useEffect(() => {
    const counts: Record<string, number> = {};
    for (const id of match.scorerIds) counts[id] = (counts[id] ?? 0) + 1;
    setScorers(counts);
  }, [match.scorerIds]);

  const isKnockout = match.phase !== 'GROUP';
  const isDraw = home === away;

  const bump = (pid: string, d: number) =>
    setScorers((s) => {
      const next = { ...s };
      next[pid] = Math.max(0, (next[pid] ?? 0) + d);
      if (next[pid] === 0) delete next[pid];
      return next;
    });

  const onSave = () => {
    const scorerIds: string[] = [];
    for (const [pid, n] of Object.entries(scorers)) for (let i = 0; i < n; i++) scorerIds.push(pid);
    save.mutate(
      {
        matchId: match.id,
        homeScore: home,
        awayScore: away,
        homePens: isKnockout && isDraw && homePens !== '' ? Number(homePens) : null,
        awayPens: isKnockout && isDraw && awayPens !== '' ? Number(awayPens) : null,
        scorerIds,
      },
      {
        onSuccess: () => {
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Marcador EN VIVO (automático por la API; aquí puedes corregirlo o forzarlo) */}
      <div className="rounded-lg border border-error/30 bg-red-50/50 p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-error">
            <span className={cn('h-2 w-2 rounded-full', isLive ? 'animate-pulse bg-error' : 'bg-font-tertiary')} />
            Marcador en vivo {isLive ? `· ${match.liveMinute != null && match.liveMinute < 999 ? match.liveMinute + "'" : 'EN VIVO'}` : '(no activo)'}
          </span>
          {isLive && (
            <button
              onClick={() => saveLive.mutate({ matchId: match.id, homeScore: home, awayScore: away, clear: true })}
              className="text-xs font-semibold text-font-secondary underline hover:text-error"
            >
              Quitar EN VIVO
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
          <span className="font-semibold">{match.homeTeam?.nameEs}</span>
          <input type="number" min={0} max={30} value={home} onChange={(e) => setHome(Math.max(0, Number(e.target.value)))}
            className="h-9 w-12 rounded border border-border text-center font-bold focus:border-error focus:outline-none" />
          <span className="text-font-tertiary">-</span>
          <input type="number" min={0} max={30} value={away} onChange={(e) => setAway(Math.max(0, Number(e.target.value)))}
            className="h-9 w-12 rounded border border-border text-center font-bold focus:border-error focus:outline-none" />
          <span className="font-semibold">{match.awayTeam?.nameEs}</span>
          <span className="ml-1 text-xs text-font-tertiary">min</span>
          <input type="number" min={0} max={130} value={liveMin} placeholder="—"
            onChange={(e) => setLiveMin(e.target.value === '' ? '' : Number(e.target.value))}
            className="h-9 w-14 rounded border border-border text-center text-xs focus:border-error focus:outline-none" />
          <button
            onClick={() => saveLive.mutate({ matchId: match.id, homeScore: home, awayScore: away, liveMinute: liveMin === '' ? null : Number(liveMin) })}
            disabled={saveLive.isPending}
            className="flex h-9 items-center gap-1.5 rounded-md bg-error px-3 text-xs font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
          >
            {saveLive.isPending ? <Loader2 size={13} className="animate-spin" /> : null} Publicar en vivo
          </button>
        </div>
        <p className="mt-1.5 text-center text-[10px] text-font-tertiary">
          Se actualiza solo cada minuto desde la API. Esto es por si quieres corregir o adelantarte. No califica puntos.
        </p>
      </div>

      {/* Resultado OFICIAL (este SÍ califica los puntos) */}
      <p className="-mb-2 text-xs font-bold uppercase tracking-wide text-font-tertiary">Resultado oficial (califica)</p>
      <p className="-mb-1 text-center text-[10px] text-font-tertiary">
        Al confirmar el feed el final del partido, el sistema lo cierra y califica los puntos solo. Usa esto para
        agregar goleadores, corregir, o cerrar eliminatorias definidas por penales (te llega un aviso push).
      </p>
      <div className="flex items-center justify-center gap-4">
        <div className="flex flex-col items-center gap-1">
          <Flag iso2={match.homeTeam?.iso2} className="h-6 w-9" />
          <span className="max-w-[120px] truncate text-xs font-semibold">{match.homeTeam?.nameEs}</span>
          <input
            type="number"
            min={0}
            max={30}
            value={home}
            onChange={(e) => setHome(Math.max(0, Number(e.target.value)))}
            className="h-14 w-16 rounded-xl border border-border text-center text-2xl font-extrabold text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <span className="text-xl font-bold text-font-tertiary">:</span>
        <div className="flex flex-col items-center gap-1">
          <Flag iso2={match.awayTeam?.iso2} className="h-6 w-9" />
          <span className="max-w-[120px] truncate text-xs font-semibold">{match.awayTeam?.nameEs}</span>
          <input
            type="number"
            min={0}
            max={30}
            value={away}
            onChange={(e) => setAway(Math.max(0, Number(e.target.value)))}
            className="h-14 w-16 rounded-xl border border-border text-center text-2xl font-extrabold text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      {/* Penales (knockout + empate) */}
      {isKnockout && isDraw && (
        <div className="flex items-center justify-center gap-2 rounded-lg bg-warning/10 p-2 text-sm">
          <span className="font-semibold text-warning">Definición por penales:</span>
          <input
            type="number"
            min={0}
            max={30}
            value={homePens}
            onChange={(e) => setHomePens(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder="0"
            className="h-8 w-12 rounded border border-border text-center text-sm focus:border-primary focus:outline-none"
          />
          <span className="text-font-tertiary">-</span>
          <input
            type="number"
            min={0}
            max={30}
            value={awayPens}
            onChange={(e) => setAwayPens(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder="0"
            className="h-8 w-12 rounded border border-border text-center text-sm focus:border-primary focus:outline-none"
          />
        </div>
      )}

      {/* Goleadores */}
      <div>
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-font-tertiary">
          <Goal size={13} /> Goleadores (para el bonus de goleador)
        </p>
        <div className="mb-2 flex h-9 items-center gap-2 rounded-md border border-border bg-white px-3">
          <Search size={13} className="text-font-tertiary" />
          <input
            value={playerFilter}
            onChange={(e) => setPlayerFilter(e.target.value)}
            placeholder="Buscar jugador en ambos planteles..."
            className="flex-1 bg-transparent text-xs focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {[
            { team: match.homeTeam, q: homePlayers },
            { team: match.awayTeam, q: awayPlayers },
          ].map(({ team, q }) => (
            <div key={team!.id} className="rounded-lg border border-border-light">
              <div className="flex items-center gap-1.5 border-b border-border-light px-2 py-1.5 text-xs font-semibold">
                <Flag iso2={team!.iso2} className="h-3.5 w-5" /> {team!.nameEs}
              </div>
              <div className="max-h-44 overflow-y-auto p-1">
                {q.isLoading && <p className="p-2 text-xs text-font-tertiary">Cargando…</p>}
                {(q.data?.items ?? [])
                  .filter(
                    (p) =>
                      !playerFilter ||
                      norm(p.jersey + ' ' + p.name).includes(norm(playerFilter)),
                  )
                  .sort((a, b) => (scorers[b.id] ?? 0) - (scorers[a.id] ?? 0) || a.number - b.number)
                  .map((p) => {
                    const c = scorers[p.id] ?? 0;
                    return (
                      <div
                        key={p.id}
                        className={cn(
                          'flex items-center gap-2 rounded px-2 py-1 text-xs',
                          c > 0 && 'bg-primary-subtle',
                        )}
                      >
                        <span className="w-5 text-right tabular-nums text-font-tertiary">{p.number}</span>
                        <span className="min-w-0 flex-1 truncate font-medium">{p.jersey}</span>
                        <button
                          onClick={() => bump(p.id, -1)}
                          className="flex h-5 w-5 items-center justify-center rounded border border-border text-font-secondary hover:border-primary"
                        >
                          –
                        </button>
                        <span className="w-4 text-center font-bold text-primary">{c || ''}</span>
                        <button
                          onClick={() => bump(p.id, 1)}
                          className="flex h-5 w-5 items-center justify-center rounded border border-border text-font-secondary hover:border-primary"
                        >
                          +
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={onSave}
        disabled={save.isPending}
        className="flex h-10 items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark disabled:opacity-60"
      >
        {save.isPending ? <Loader2 size={15} className="animate-spin" /> : saved ? <Check size={15} /> : <Save size={15} />}
        {saved ? 'Resultado guardado y puntos calculados' : 'Guardar resultado'}
      </button>
    </div>
  );
}

function ScoringTab() {
  const { data: cfg } = useWcScoring();
  const save = useWcSaveScoring();
  const [form, setForm] = useState<WcScoringConfig | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (cfg) setForm(cfg);
  }, [cfg]);

  if (!form) return <div className="h-40 rounded-xl border border-border brand-shimmer" />;

  const upd = (k: keyof WcScoringConfig, v: number) => setForm({ ...form, [k]: v });

  const numField = (label: string, key: keyof WcScoringConfig, hint?: string) => (
    <div>
      <label className="mb-1 block text-sm font-medium text-font-primary">{label}</label>
      <input
        type="number"
        step="any"
        min={0}
        value={form[key] as number}
        onChange={(e) => upd(key, Number(e.target.value))}
        className="h-10 w-full rounded-md border border-border px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
      {hint && <p className="mt-0.5 text-[11px] text-font-tertiary">{hint}</p>}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-base font-bold text-font-primary">Puntos por partido</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {numField('Marcador exacto', 'pointsExact')}
          {numField('Ganador + diferencia', 'pointsOutcomeGd')}
          {numField('Sólo ganador (1X2)', 'pointsOutcome')}
          {numField('Goles exactos de un equipo', 'pointsTeamGoals', 'bonus')}
          {numField('Goleador acertado', 'pointsScorer', 'bonus')}
          {numField('Minutos de cierre antes del inicio', 'lockMinutesBefore', '0 = al pitazo')}
          {numField(
            'Recordatorio push antes del partido (min)',
            'reminderMinutesBefore',
            '0 = sin recordatorios · avisa a quienes no han pronosticado',
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-base font-bold text-font-primary">Multiplicador por ronda</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {numField('Fase de grupos', 'multGroup')}
          {numField('Dieciseisavos', 'multR32')}
          {numField('Octavos', 'multR16')}
          {numField('Cuartos', 'multQF')}
          {numField('Semifinal', 'multSF')}
          {numField('Final / 3er lugar', 'multFinal')}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-base font-bold text-font-primary">Bonos de torneo</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {numField('Campeón', 'pointsChampion')}
          {numField('Subcampeón', 'pointsFinalist')}
          {numField('Bota de Oro', 'pointsGoldenBoot')}
        </div>
      </div>

      <button
        onClick={() =>
          save.mutate(form, {
            onSuccess: () => {
              setSaved(true);
              setTimeout(() => setSaved(false), 2000);
            },
          })
        }
        disabled={save.isPending}
        className="flex h-11 items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark disabled:opacity-60"
      >
        {save.isPending ? <Loader2 size={16} className="animate-spin" /> : saved ? <Check size={16} /> : <Save size={16} />}
        {saved ? 'Guardado · recalcula para aplicar a partidos ya jugados' : 'Guardar tabla de puntos'}
      </button>
    </div>
  );
}

function NotifyTab() {
  const notify = useWcNotify();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const onSend = () => {
    if (!title.trim() || !body.trim()) return;
    notify.mutate(
      { title: title.trim(), body: body.trim(), url: '/mundial' },
      {
        onSuccess: (r: any) =>
          setResult(`Enviado a ${r.sent} de ${r.subscribers} dispositivos suscritos.`),
        onError: () => setResult('No se pudo enviar. Verifica la configuración de notificaciones.'),
      },
    );
  };

  return (
    <div className="mx-auto w-full max-w-lg rounded-xl border border-border bg-white p-5 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <Bell size={18} className="text-primary" />
        <h3 className="text-base font-bold text-font-primary">Enviar notificación push</h3>
      </div>
      <p className="mb-4 text-xs text-font-secondary">
        Llega a todos los colaboradores que instalaron la app y activaron las notificaciones.
      </p>
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-font-primary">Título</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="¡Hoy juega México!"
            className="h-10 w-full rounded-md border border-border px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-font-primary">Mensaje</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder="No olvides hacer tu pronóstico antes de las 3:00 PM."
            className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <button
          onClick={onSend}
          disabled={notify.isPending || !title.trim() || !body.trim()}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark disabled:opacity-50"
        >
          {notify.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          Enviar a todos
        </button>
        {result && (
          <p className="rounded-lg bg-bg p-2.5 text-center text-xs font-medium text-font-secondary">{result}</p>
        )}
      </div>
    </div>
  );
}
